import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { configuration } from '../../core/configuration';
import {
	ColumnErrorTypes,
	ColumnConfigItemEntity,
} from './column-config-item.entity';

export const HASHED_COLUMN_TYPES = [ColumnType.Str];

export enum ColumnErrorKey {
	NoColumnsSelectedInSource = 'no-columns-selected-in-source',
	InvalidColumns = 'invalid-columns',
}

export type ColumnListError = {
	key: ColumnErrorKey;
};

interface IColumnConfigListExtra
	extends IListColumnConfig<ColumnConfigItemEntity> {
	isFixed?: boolean;
	isSelectable?: boolean;
	isSearchable?: boolean;
	isInGroup?: boolean;
	isVisible?: boolean;
	canBeHidden?: boolean;
	canBeReordered?: boolean;
	canBeChosen?: boolean;
	format?: string;
	visibleIndex?: number;
}

export interface IColumnConfigListEntity
	extends IListAttrs<ColumnConfigItemEntity> {
	availableColumns: Map<string, IColumnConfigListExtra>;
	isAllSelectedInSource: boolean;
	isAllActive: boolean;
	isAllHashed: boolean;
	errors: Map<ColumnErrorKey, ColumnListError>;
}

const entityDefaults: IColumnConfigListEntity = {
	elements: Map(),
	availableColumns: Map(),
	isAllSelectedInSource: false,
	isAllActive: false,
	isAllHashed: false,
	errors: Map(),
};

export class ColumnConfigListEntity
	implements
		IColumnConfigListEntity,
		IListOperations<ColumnConfigListEntity, ColumnConfigItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;
	public isAllSelectedInSource = entityDefaults.isAllSelectedInSource;
	public isAllActive = entityDefaults.isAllActive;
	public isAllHashed = entityDefaults.isAllHashed;
	public errors = entityDefaults.errors;

	public static build(
		availableCols: Array<IColumnConfigListExtra>,
		withErrors?: Array<[ColumnErrorKey, ColumnListError]>
	): ColumnConfigListEntity {
		const inst = new ColumnConfigListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IColumnConfigListExtra]>());
		inst.availableColumns = Map(cols);
		inst.errors = Map(withErrors);
		return inst.detectErrors();
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<ColumnConfigItemEntity>
	> {
		return this.availableColumns;
	}

	public setElements(
		elems: Map<string, ColumnConfigItemEntity>
	): ColumnConfigListEntity {
		const list = produce(this, (draft: ColumnConfigListEntity) => {
			draft.elements = elems;
		});
		return list.detectErrors();
	}

	public updateIsSelectedInSource(
		ids: Array<string>,
		value: boolean
	): ColumnConfigListEntity {
		let elems = this.elements;
		ids.map((id) => {
			const elem = this.elements.get(id);
			if (!elem) {
				return;
			}
			const updatedElem = elem.setIsSelectedInSource(value);

			elems = elems.set(id, updatedElem);
		});

		const updatedElems = elems.map((elem) => {
			const errors = this.getColumnErrorType(
				elem.errors,
				elem.alias,
				elem.id,
				elems
			);
			if (errors !== elem.errors) {
				return elem.setErrors(errors);
			}
			return elem;
		});

		return this.setElements(updatedElems);
	}

	public updateIsActive(
		ids: Array<string>,
		value: boolean
	): ColumnConfigListEntity {
		let elems = this.elements;
		ids.map((id) => {
			const elem = this.elements.get(id);
			if (!elem) {
				return;
			}
			const updatedElem = elem.setIsActive(value);

			elems = elems.set(id, updatedElem);
		});

		return this.setElements(elems);
	}

	public updateIsHashed(
		ids: Array<string>,
		value: boolean
	): ColumnConfigListEntity {
		let elems = this.elements;
		ids.map((id) => {
			const elem = this.elements.get(id);
			if (!elem) {
				return;
			}
			const updatedElem = elem.setIsHashed(value);

			elems = elems.set(id, updatedElem);
		});
		return this.setElements(elems);
	}

	public updateAlias(id: string, value: string): ColumnConfigListEntity {
		const elem = this.elements.get(id);

		if (!elem) {
			return this;
		}

		const updatedElem = elem.setAlias(value);

		const elems = this.elements.set(id, updatedElem);

		const updatedElems = elems.map((elem) => {
			const errors = this.getColumnErrorType(
				elem.errors,
				elem.alias,
				elem.id,
				elems
			);
			if (errors !== elem.errors) {
				return elem.setErrors(errors);
			}
			return elem;
		});
		return this.setElements(updatedElems);
	}

	public updateDescription(id: string, value: string): ColumnConfigListEntity {
		const elem = this.elements.get(id);

		if (!elem) {
			return this;
		}

		let errors;
		// MAX_LENGTH_DESC
		if (value.length > configuration.maxLengthDescription) {
			errors = elem.errors.add(ColumnErrorTypes.MAX_LENGTH_DESC);
		} else {
			errors = elem.errors.delete(ColumnErrorTypes.MAX_LENGTH_DESC);
		}

		const updatedElem = elem.setDescription(value).setErrors(errors);

		const elems = this.elements.set(id, updatedElem);

		return this.setElements(elems);
	}

	public isEqualElementsTo(
		columnElements: Map<string, ColumnConfigItemEntity>
	): boolean {
		if (columnElements.size !== this.elements.size) {
			return false;
		}

		const keys = Array.from(columnElements.keys());
		let i = 0;
		while (i < columnElements.size) {
			const key = keys[i];
			const columElem = columnElements.get(key);
			const elem = this.elements.get(key);
			if (!elem || !columElem || !columElem.isEqualTo(elem)) {
				return false;
			}
			i++;
		}
		return true;
	}

	private detectErrors(): ColumnConfigListEntity {
		const errors = new Array<[ColumnErrorKey, ColumnListError]>();

		const invalidColumnCount = this.elements.filter(
			(elem) => elem.errors.size !== 0 && elem.isSelectedInSource
		);

		if (invalidColumnCount.size !== 0 || this.elements.size === 0) {
			errors.push([
				ColumnErrorKey.InvalidColumns,
				{
					key: ColumnErrorKey.InvalidColumns,
				},
			]);
		}

		const selectedInSourceCount = this.elements.reduce<number>(
			(acc, curr) => (curr.isSelectedInSource ? acc + 1 : acc),
			0
		);

		if (selectedInSourceCount === 0 || this.elements.size === 0) {
			errors.push([
				ColumnErrorKey.NoColumnsSelectedInSource,
				{
					key: ColumnErrorKey.NoColumnsSelectedInSource,
				},
			]);
		}

		if (this.elements.size === 0) {
			return produce(this, (draft: ColumnConfigListEntity) => {
				draft.errors = Map(errors);
			});
		}

		const activeCount = this.elements.reduce<number>(
			(acc, curr) => (curr.isActive ? acc + 1 : acc),
			0
		);

		const hashedCount = this.elements.reduce<number>(
			(acc, curr) => (curr.isHashed && curr.isActive ? acc + 1 : acc),
			0
		);

		const allHashedElements = this.elements.filter(
			(el) => el.isActive && HASHED_COLUMN_TYPES.includes(el.columnType)
		);

		const enabledSelectedInSourceColumnCount = this.elements.reduce<number>(
			(acc, curr) =>
				curr.isSelectedInSource && curr.referencedBy.total === 0
					? acc + 1
					: acc,
			0
		);

		const allSelectableSourceElements = this.elements.filter(
			(el) => el.referencedBy.total === 0
		);

		return produce(this, (draft: ColumnConfigListEntity) => {
			draft.errors = Map(errors);
			draft.isAllSelectedInSource =
				enabledSelectedInSourceColumnCount ===
					allSelectableSourceElements.size &&
				allSelectableSourceElements.size !== 0;
			draft.isAllActive = activeCount === this.elements.size;
			draft.isAllHashed =
				hashedCount === allHashedElements.size && allHashedElements.size !== 0;
		});
	}

	private getColumnErrorType(
		currentErrors: Set<ColumnErrorTypes>,
		alias: string,
		id: string,
		elems: Map<string, ColumnConfigItemEntity>
	): Set<ColumnErrorTypes> {
		const duplicatedElems = elems.filter(
			(elem) =>
				elem.isSelectedInSource &&
				elem.alias &&
				elem.alias === alias &&
				elem.id !== id
		);

		// EMPTY
		if (!alias) {
			currentErrors = currentErrors.add(ColumnErrorTypes.EMPTY);
		} else {
			currentErrors = currentErrors.delete(ColumnErrorTypes.EMPTY);
		}

		// DUPLICATED
		if (duplicatedElems.size !== 0) {
			currentErrors = currentErrors.add(ColumnErrorTypes.DUPLICATED);
		} else {
			currentErrors = currentErrors.delete(ColumnErrorTypes.DUPLICATED);
		}

		return currentErrors;
	}
}
